// src/pages/CommissionerPage.jsx
// Commissioner (host-only) settings panel
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue, update, get } from "firebase/database";
import { FANTASY_WEEKS } from "../services/iplSchedule";

export function CommissionerPage({ leagueCode }) {
  const uid = auth.currentUser?.uid;
  const [league, setLeague] = useState(null);
  const [room,   setRoom]   = useState(null);
  const [status, setStatus] = useState({});

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  const isHost = league?.ownerUid === uid;

  const teams = useMemo(() => room?.teams || [], [room]);
  const playerMap = useMemo(() => room?.players || {}, [room]);
  const slots = useMemo(() => room?.slots || {}, [room]);
  const currentWeek = league?.season?.week || 1;

  const teamCustom = room?.teamCustom || {};
  const getTeamName = (slotId) => teamCustom[slotId]?.name || slots[slotId]?.name || slotId;

  const setMsg = (key, msg, isError = false) => setStatus(prev => ({ ...prev, [key]: { msg, isError } }));

  // ── 1. Score adjustment ──────────────────────────────────────
  const [scoreTeam, setScoreTeam] = useState("");
  const [scoreWeek, setScoreWeek] = useState(currentWeek);
  const [scoreAdj,  setScoreAdj]  = useState(0);

  const applyScoreAdj = async () => {
    if (!scoreTeam) return setMsg("score", "Select a team.", true);
    const code = leagueCode.toUpperCase();
    const path = `leagues/${code}/season/teamPoints/${scoreWeek}/${scoreTeam}`;
    try {
      const snap = await get(ref(db, path));
      const current = snap.val() || 0;
      await update(ref(db), { [path]: current + Number(scoreAdj) });
      setMsg("score", `✓ Adjusted ${getTeamName(scoreTeam)}'s Week ${scoreWeek} score by ${scoreAdj > 0 ? "+" : ""}${scoreAdj}`);
    } catch (e) { setMsg("score", "Failed: " + e.message, true); }
  };

  // ── 2. Week lock time ────────────────────────────────────────
  const [lockWeek,  setLockWeek]  = useState(currentWeek);
  const [lockInput, setLockInput] = useState("");

  const setWeekLock = async () => {
    const code = leagueCode.toUpperCase();
    const ms = new Date(lockInput).getTime();
    if (isNaN(ms)) return setMsg("lock", "Invalid date/time.", true);
    try {
      await update(ref(db), {
        [`leagues/${code}/season/weekStartTime`]: ms,
        [`leagues/${code}/season/week`]: lockWeek,
      });
      setMsg("lock", `✓ Week ${lockWeek} lock set to ${new Date(ms).toLocaleString()}`);
    } catch (e) { setMsg("lock", "Failed: " + e.message, true); }
  };

  const setLockFromIPL = async (weekNum) => {
    const code = leagueCode.toUpperCase();
    const iplWeek = FANTASY_WEEKS.find(w => w.week === weekNum);
    if (!iplWeek) return;
    try {
      await update(ref(db), {
        [`leagues/${code}/season/weekStartTime`]: iplWeek.startTime,
        [`leagues/${code}/season/week`]: weekNum,
      });
      setMsg("lock", `✓ Set to ${iplWeek.label}`);
    } catch (e) { setMsg("lock", "Failed: " + e.message, true); }
  };

  // ── 3. Force add / drop player ───────────────────────────────
  const [adDropTeam,   setAdDropTeam]   = useState("");
  const [adDropPlayer, setAdDropPlayer] = useState("");
  const [adDropMode,   setAdDropMode]   = useState("add"); // "add" | "drop"

  const availablePlayers = useMemo(() => {
    if (adDropMode === "drop") {
      const team = teams.find(t => t.name === adDropTeam);
      return (team?.squadIds || []).map(id => playerMap[String(id)]).filter(Boolean);
    }
    return Object.values(playerMap).filter(p => !p.drafted);
  }, [adDropMode, adDropTeam, teams, playerMap]);

  const forceAddDrop = async () => {
    if (!adDropTeam || !adDropPlayer) return setMsg("addrop", "Select team and player.", true);
    const code = leagueCode.toUpperCase();
    try {
      const tIdx = teams.findIndex(t => t.name === adDropTeam);
      if (tIdx < 0) return setMsg("addrop", "Team not found.", true);
      const team = teams[tIdx];
      const squadIds = [...(team.squadIds || [])];
      const pid = Number(adDropPlayer);

      if (adDropMode === "drop") {
        const newSquad = squadIds.filter(id => id !== pid);
        await update(ref(db), {
          [`rooms/${code}/teams/${tIdx}/squadIds`]: newSquad,
          [`rooms/${code}/players/${adDropPlayer}/drafted`]: false,
          [`rooms/${code}/players/${adDropPlayer}/owner`]: null,
        });
        setMsg("addrop", `✓ Dropped player from ${getTeamName(adDropTeam)}`);
      } else {
        if (!squadIds.includes(pid)) squadIds.push(pid);
        await update(ref(db), {
          [`rooms/${code}/teams/${tIdx}/squadIds`]: squadIds,
          [`rooms/${code}/players/${adDropPlayer}/drafted`]: true,
          [`rooms/${code}/players/${adDropPlayer}/owner`]: adDropTeam,
        });
        setMsg("addrop", `✓ Added player to ${getTeamName(adDropTeam)}`);
      }
    } catch (e) { setMsg("addrop", "Failed: " + e.message, true); }
  };

  // ── 4. Re-sync a past match ──────────────────────────────────
  const [resyncMatchNum, setResyncMatchNum] = useState("");

  const queueResync = async () => {
    const num = Number(resyncMatchNum);
    if (!num || num < 1 || num > 70) return setMsg("resync", "Enter a valid match number (1–70).", true);
    const code = leagueCode.toUpperCase();
    try {
      const idSnap = await get(ref(db, `leagues/${code}/config/matchIds/${num}`));
      const cricbuzzId = idSnap.val();
      if (!cricbuzzId) return setMsg("resync", `No Cricbuzz ID stored for match ${num}. It may not have been auto-synced yet.`, true);
      await update(ref(db), { [`leagues/${code}/config/resyncQueue/${num}`]: String(cricbuzzId) });
      setMsg("resync", `✓ Match ${num} queued — will be re-synced on the next cron run (during a live match).`);
    } catch (e) { setMsg("resync", "Failed: " + e.message, true); }
  };

  // ── 5. Advance week ─────────────────────────────────────────
  const advanceWeek = async () => {
    const code = leagueCode.toUpperCase();
    const next = currentWeek + 1;
    try {
      await update(ref(db), { [`leagues/${code}/season/week`]: next });
      setMsg("week", `✓ Advanced to Week ${next}`);
    } catch (e) { setMsg("week", "Failed: " + e.message, true); }
  };

  if (!isHost) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Commissioner Only</div>
        <div style={{ color: "var(--text-dim)", fontSize: 13 }}>Only the league host can access these settings.</div>
      </div>
    );
  }

  const Section = ({ title, icon, children }) => (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="panel-head">
        <div style={{ fontWeight: 700, fontSize: 15 }}>{icon} {title}</div>
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );

  const StatusMsg = ({ k }) => status[k] ? (
    <div style={{ marginTop: 8, fontSize: 12, color: status[k].isError ? "var(--red)" : "var(--green)" }}>
      {status[k].msg}
    </div>
  ) : null;

  const teamSelect = (val, onChange, placeholder = "Select team") => (
    <select value={val} onChange={e => onChange(e.target.value)}
      style={{ flex: 1, minWidth: 160, padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }}>
      <option value="">{placeholder}</option>
      {teams.map(t => <option key={t.name} value={t.name}>{getTeamName(t.name)}</option>)}
    </select>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 22, fontFamily: "'Playfair Display', Georgia, serif" }}>
          ⚙️ Commissioner
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>
          League: {leagueCode} · Week {currentWeek} · {teams.length} teams
        </div>
      </div>

      {/* 1. Current week */}
      <Section title="Week Control" icon="📅">
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 14 }}>
            Current week: <strong style={{ color: "var(--gold)" }}>Week {currentWeek}</strong>
          </div>
          <button className="primary" onClick={advanceWeek} style={{ fontSize: 12 }}>
            Advance to Week {currentWeek + 1} →
          </button>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Set week lock time (lineup deadline)</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={lockWeek} onChange={e => setLockWeek(Number(e.target.value))}
              style={{ padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(w => <option key={w} value={w}>Week {w}</option>)}
            </select>
            <input type="datetime-local" value={lockInput} onChange={e => setLockInput(e.target.value)}
              style={{ padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }} />
            <button onClick={setWeekLock}>Set Lock</button>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-dim)", marginBottom: 6 }}>Or use IPL 2026 schedule:</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FANTASY_WEEKS.slice(0, 6).map(w => (
              <button key={w.week} className="mini" onClick={() => setLockFromIPL(w.week)} style={{ fontSize: 10 }}>
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <StatusMsg k="lock" />
        <StatusMsg k="week" />
      </Section>

      {/* 2. Score adjustment */}
      <Section title="Adjust Score" icon="📊">
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 12 }}>
          Add or subtract points from a team's weekly total.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {teamSelect(scoreTeam, setScoreTeam)}
          <select value={scoreWeek} onChange={e => setScoreWeek(Number(e.target.value))}
            style={{ padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(w => <option key={w} value={w}>Week {w}</option>)}
          </select>
          <input type="number" value={scoreAdj} onChange={e => setScoreAdj(e.target.value)}
            placeholder="±points" style={{ width: 90, padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }} />
          <button className="primary" onClick={applyScoreAdj}>Apply</button>
        </div>
        <StatusMsg k="score" />
      </Section>

      {/* 3. Force add / drop */}
      <Section title="Force Add / Drop Player" icon="🔄">
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 12 }}>
          Move any player to or from any team's squad.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <button className={adDropMode === "add" ? "primary" : "mini"} onClick={() => setAdDropMode("add")}>Add</button>
          <button className={adDropMode === "drop" ? "primary" : "mini"} onClick={() => setAdDropMode("drop")}
            style={{ background: adDropMode === "drop" ? "var(--red)" : undefined, borderColor: adDropMode === "drop" ? "var(--red)" : undefined, color: adDropMode === "drop" ? "#fff" : undefined }}>
            Drop
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {teamSelect(adDropTeam, setAdDropTeam)}
          <select value={adDropPlayer} onChange={e => setAdDropPlayer(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }}>
            <option value="">Select player</option>
            {availablePlayers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role} · {p.team})</option>)}
          </select>
          <button className="primary" onClick={forceAddDrop}>
            {adDropMode === "add" ? "Add to Squad" : "Drop from Squad"}
          </button>
        </div>
        <StatusMsg k="addrop" />
      </Section>

      {/* 4. Re-sync match */}
      <Section title="Re-sync Past Match" icon="🔄">
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 12 }}>
          If a player's scores didn't come through (e.g. name mismatch that's since been fixed),
          queue a specific IPL match number to be re-processed on the next cron run.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="number" min="1" max="70" value={resyncMatchNum}
            onChange={e => setResyncMatchNum(e.target.value)}
            placeholder="Match # (1–70)"
            style={{ width: 140, padding: "7px 10px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13 }}
          />
          <button className="primary" onClick={queueResync}>Queue Re-sync</button>
        </div>
        <StatusMsg k="resync" />
      </Section>

      {/* 5. League info */}
      <Section title="League Info" icon="ℹ️">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          {[
            { label: "League Code", value: leagueCode },
            { label: "Phase", value: league?.phase || "—" },
            { label: "Teams", value: teams.length },
            { label: "Current Week", value: currentWeek },
            { label: "Week Lock", value: league?.season?.weekStartTime ? new Date(league.season.weekStartTime).toLocaleDateString() : "Not set" },
            { label: "Season Started", value: league?.season?.finalizedAt ? new Date(league.season.finalizedAt).toLocaleDateString() : "No" },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: "var(--bg2)", borderRadius: "var(--radius-sm)", padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{value}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
